import { useEffect, useRef } from 'react';
import { useAuthOptional } from '@/contexts/AuthContext';
import { useProfile } from '@/hooks/useProfile';
import { useConciergeApplications } from '@/hooks/useConciergeApplications';
import { loadConciergeDraft, clearConciergeDraft } from '@/components/pricing/conciergeDraft';
import { useToast } from '@/hooks/use-toast';

/**
 * Auto-submits a saved concierge draft after signup.
 * Should be mounted once in the app layout or root.
 */
export function useConciergeResume() {
  // Optional variant: this hook runs at the app root, so a missing provider
  // (e.g. a stale hot-reload module boundary) must not throw and blank the app.
  const user = useAuthOptional()?.user ?? null;
  const { profile } = useProfile();
  const { submitApplication, myApplications } = useConciergeApplications();
  const { toast } = useToast();
  const attempted = useRef(false);

  useEffect(() => {
    if (!user || !profile || attempted.current) return;
    if (submitApplication.isPending) return;

    const draft = loadConciergeDraft();
    if (!draft?.data || !draft.packageId || !draft.packageName) return;

    // Don't submit if user already has an application
    if (myApplications && myApplications.length > 0) {
      clearConciergeDraft();
      return;
    }

    // Don't submit if concierge status is already set
    if (profile.concierge_status && profile.concierge_status !== 'none') {
      clearConciergeDraft();
      return;
    }

    attempted.current = true;

    submitApplication.mutateAsync({
      intakeData: draft.data,
      packageId: draft.packageId,
      packageName: draft.packageName,
    }).then(() => {
      clearConciergeDraft();
      toast({
        title: "Concierge Application Submitted",
        description: "Your preferences have been saved. Our team will review your application.",
      });
    }).catch((err) => {
      console.error('[ConciergeResume] Failed to submit draft:', err);
      // Don't clear draft on error so they can retry
      attempted.current = false;
    });
  }, [user, profile, myApplications, submitApplication, toast]);
}
